// biome-ignore-all lint/a11y: TODO - address a11y issues later
import { format } from "date-fns";
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  Loader2,
  Lock,
  Pause,
  Play,
  Send,
  Trash2,
} from "lucide-react";
import * as React from "react";
import { useNavigate } from "react-router";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DonutProgress } from "@/components/ui/donut-progress";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { DeepResearchReportMetadata } from "@/types/deep-research-reports";
import { MAX_GENERATIONS_LIMIT } from "./drr-table-common";
import { EntitySelector } from "./entity-selector";

interface DatasetReportLayoutProps {
  mode: "editor" | "viewer";
  report: DeepResearchReportMetadata;
  children: React.ReactNode;

  // Editor-specific props
  isEditingName?: boolean;
  isLocked?: boolean;
  currentRecordCount?: number;
  datasetType?: "static" | "dynamic";
  entityType?: "opportunity" | "account" | "person";
  aiColumnsCount?: number;

  // Editor callbacks
  onEditName?: () => void;
  onSaveEditName?: (name: string) => void;
  onCancelEditName?: () => void;
  onApply?: () => void;
  onSubmit?: () => void;
  onCreateTasks?: () => void;
  onDelete?: () => void;
  onTypeChange?: (type: "static" | "dynamic") => void;
  onEntityTypeChange?: (
    entityType: "opportunity" | "account" | "person",
  ) => void;

  // Feature flags
  showCreateTasksButton?: boolean;
  isCreatingTasks?: boolean;
  onPause?: () => void;
  onResume?: () => void;

  // Loading states
  isUpdatingName?: boolean;
  isApplying?: boolean;
  isSubmitting?: boolean;
  isDeleting?: boolean;

  // Validation states
  showValidationAlerts?: boolean;
  submitDisabled?: boolean;
}

const getStatusBadge = (report: DeepResearchReportMetadata) => {
  const statusConfig = {
    active: { label: "Active", variant: "default" as const, icon: Activity },
    complete: { label: "Complete", variant: "opportunity-won" as const },
    queued: { label: "Processing", variant: "opportunity-active" as const },
    pending_review: {
      label: "Pending Review",
      variant: "secondary" as const,
    },
    failed: { label: "Failed", variant: "destructive" as const },
    draft: { label: "Draft", variant: "secondary" as const },
    paused: { label: "Paused", variant: "secondary" as const },
  };

  const config = statusConfig[report.status as keyof typeof statusConfig];
  if (!config) {
    return <Badge variant="default">{report.status}</Badge>;
  }

  return (
    <Badge variant={config.variant}>
      {"icon" in config && config.icon && (
        <config.icon className="mr-1 h-3 w-3" />
      )}
      {config.label}
    </Badge>
  );
};

export function DatasetReportLayout({
  mode,
  report,
  children,
  isEditingName = false,
  isLocked = false,
  currentRecordCount = 0,
  datasetType,
  entityType,
  aiColumnsCount = 0,
  onEditName,
  onSaveEditName,
  onCancelEditName,
  onApply,
  onSubmit,
  onCreateTasks,
  onDelete,
  onTypeChange,
  onEntityTypeChange,
  showCreateTasksButton = false,
  isCreatingTasks = false,
  onPause,
  onResume,
  isUpdatingName = false,
  isApplying = false,
  isSubmitting = false,
  isDeleting = false,
  showValidationAlerts = false,
  submitDisabled = false,
}: DatasetReportLayoutProps) {
  const navigate = useNavigate();
  // Localize editing input state to prevent re-rendering heavy children on each keystroke
  const [draftName, setDraftName] = React.useState<string>(report.name);

  React.useEffect(() => {
    if (isEditingName) {
      setDraftName(report.name);
    }
    // Reset to current name when starting to edit
  }, [isEditingName, report.name]);

  React.useEffect(() => {
    if (!isEditingName) {
      setDraftName(report.name);
    }
  }, [isEditingName, report.name]);

  const handleSaveName = React.useCallback((): void => {
    const trimmed = draftName.trim();
    if (trimmed.length === 0) return;
    // Only save if the name actually changed
    if (trimmed !== report.name.trim()) {
      // Pass the new name directly to the save handler
      onSaveEditName?.(trimmed);
    } else {
      // If name didn't change, just cancel the edit mode
      onCancelEditName?.();
    }
  }, [draftName, onSaveEditName, onCancelEditName, report.name]);

  const handleCancelEdit = React.useCallback((): void => {
    setDraftName(report.name);
    onCancelEditName?.();
  }, [onCancelEditName, report.name]);

  return (
    <div className="flex h-full flex-col space-y-2">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/deep-research")}
          className={mode === "viewer" ? "mb-2" : ""}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Datasets
        </Button>

        {/* Action buttons - Editor mode only */}
        {mode === "editor" && (
          <div className="flex items-center space-x-2">
            {datasetType === "dynamic" &&
              report.status === "active" &&
              onPause && (
                <Button variant="outline" size="sm" onClick={onPause}>
                  <Pause className="mr-2 h-4 w-4" />
                  Pause Refresh
                </Button>
              )}
            {datasetType === "dynamic" &&
              report.status === "paused" &&
              onResume && (
                <Button variant="outline" size="sm" onClick={onResume}>
                  <Play className="mr-2 h-4 w-4" />
                  Resume Refresh
                </Button>
              )}
          </div>
        )}
      </div>

      {/* Title Section */}
      {mode === "editor" ? (
        /* Editor: Editable name row with submit button */
        <div className="flex items-center justify-between">
          {isEditingName ? (
            <div className="flex items-center space-x-2">
              <Input
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSaveName()}
                className="w-80"
                autoFocus
              />
              <Button
                size="sm"
                onClick={handleSaveName}
                disabled={isUpdatingName}
              >
                {isUpdatingName ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Save
              </Button>
              <Button size="sm" variant="ghost" onClick={handleCancelEdit}>
                Cancel
              </Button>
            </div>
          ) : (
            <h1
              className={cn(
                "text-2xl font-bold",
                !isLocked &&
                  "cursor-pointer hover:text-muted-foreground transition-colors",
              )}
              onClick={() => !isLocked && onEditName?.()}
            >
              {report.name}
            </h1>
          )}

          {/* Action buttons based on status */}
          <div className="flex items-center gap-2">
            {/* Draft status buttons */}
            {report.status === "draft" && (
              <>
                {onApply && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={onApply}
                    disabled={isApplying}
                  >
                    {isApplying ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : null}
                    Apply Filters
                  </Button>
                )}
                {onSubmit && (
                  <Button
                    size="sm"
                    onClick={onSubmit}
                    disabled={submitDisabled || isSubmitting}
                  >
                    {isSubmitting ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="mr-2 h-4 w-4" />
                    )}
                    Submit for Review
                  </Button>
                )}
                {onDelete && (
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={onDelete}
                    disabled={isDeleting}
                  >
                    {isDeleting ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="mr-2 h-4 w-4" />
                    )}
                    Delete Dataset
                  </Button>
                )}
              </>
            )}

            {/* Pending review status buttons */}
            {report.status === "pending_review" &&
              showCreateTasksButton &&
              onCreateTasks && (
                <Button
                  size="sm"
                  onClick={onCreateTasks}
                  disabled={isCreatingTasks}
                  variant="default"
                >
                  {isCreatingTasks ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Play className="mr-2 h-4 w-4" />
                  )}
                  Create Tasks
                </Button>
              )}
          </div>
        </div>
      ) : (
        /* Viewer: Title with badges and metadata */
        <>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold">{report.name}</h1>
              {getStatusBadge(report)}
              <Badge variant="secondary" className="flex items-center gap-1.5">
                {report.dataset_type === "dynamic" && (
                  <Activity className="h-3.5 w-3.5" />
                )}
                <span className="capitalize">{report.dataset_type}</span>
              </Badge>
              {(report.status === "pending_review" ||
                report.status === "queued") &&
                report.completion !== undefined &&
                report.completion !== null && (
                  <DonutProgress
                    value={report.completion}
                    size={36}
                    strokeWidth={4}
                    showLabel={true}
                  />
                )}
            </div>
          </div>

          {/* Metadata */}
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-4">
            <span>
              Created by {report.created_by.name || report.created_by.email}
            </span>
            <span>•</span>
            <span>
              {format(new Date(report.created_at), "MMM d, yyyy 'at' h:mm a")}
            </span>
            {report.record_count !== undefined &&
              report.record_count !== null && (
                <>
                  <span>•</span>
                  <span>{report.record_count.toLocaleString()} records</span>
                </>
              )}
            {report.ai_fields && report.ai_fields.length > 0 && (
              <>
                <span>•</span>
                <span>{report.ai_fields.length} AI fields</span>
              </>
            )}
          </div>
        </>
      )}

      {/* Alerts - Editor mode only */}
      {mode === "editor" && showValidationAlerts && (
        <>
          {/* Alert for no AI columns */}
          {report.status === "draft" && aiColumnsCount === 0 && (
            <Alert>
              <AlertDescription>
                Please add at least one AI column to process this dataset. AI
                columns define what information will be extracted from each
                record.
              </AlertDescription>
            </Alert>
          )}

          {/* Alert for max generations limit */}
          {report.status === "draft" &&
            currentRecordCount * aiColumnsCount > MAX_GENERATIONS_LIMIT && (
              <Alert variant="destructive">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    This dataset would generate{" "}
                    {(currentRecordCount * aiColumnsCount).toLocaleString()} AI
                    field values ({currentRecordCount.toLocaleString()} records
                    × {aiColumnsCount} AI fields), which exceeds the maximum
                    limit of {MAX_GENERATIONS_LIMIT.toLocaleString()}{" "}
                    generations. Please use fewer AI fields or reduce the record
                    count to stay within the limit.
                  </AlertDescription>
                </div>
              </Alert>
            )}

          {/* Alert for locked state */}
          {isLocked && (
            <Alert>
              <Lock className="h-4 w-4" />
              <AlertDescription>
                This dataset is locked for editing. {""}
                {report.status === "queued" &&
                  "It is currently being processed."}
                {(report.status === "active" || report.status === "paused") &&
                  "Dynamic datasets cannot be edited after submission."}
              </AlertDescription>
            </Alert>
          )}
        </>
      )}

      {/* Controls - Editor mode only */}
      {mode === "editor" && (
        <div className="flex items-center space-x-3">
          <EntitySelector
            value={entityType || "opportunity"}
            onChange={onEntityTypeChange || (() => {})}
            disabled={isLocked}
          />

          {/* Dataset type selector */}
          {report.status === "draft" && onTypeChange && (
            <Select
              value={datasetType || "static"}
              onValueChange={onTypeChange}
            >
              <SelectTrigger className="w-[150px] bg-white dark:bg-[hsl(195_30%_12%)] rounded-lg corner-smooth-lg font-medium">
                <SelectValue>
                  <div className="flex items-center gap-2">
                    {datasetType === "dynamic" && (
                      <Activity className="h-4 w-4" />
                    )}
                    {datasetType === "static" && <div className="h-4 w-4" />}
                    <span>
                      {datasetType === "static" ? "Static" : "Dynamic"}
                    </span>
                  </div>
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="static">
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4" />
                    <span>Static</span>
                  </div>
                </SelectItem>
                <SelectItem value="dynamic" disabled>
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-2">
                      <Activity className="h-4 w-4" />
                      <span>Dynamic</span>
                    </div>
                    <Badge variant="secondary" className="ml-2 text-xs">
                      Coming Soon
                    </Badge>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          )}
          {report.status !== "draft" && (
            <Badge
              variant="secondary"
              className="flex items-center gap-1.5 px-3 py-1"
            >
              {datasetType === "dynamic" && (
                <Activity className="h-3.5 w-3.5" />
              )}
              <span className="capitalize">{datasetType}</span>
            </Badge>
          )}

          {/* Status badge */}
          {getStatusBadge(report)}
        </div>
      )}

      {/* Main content */}
      <div className="relative flex min-h-0 flex-grow flex-col">{children}</div>
    </div>
  );
}
